import { useEffect, type ReactNode } from 'react';
import { Zap, Monitor, Plug, Cpu, Tv2 } from 'lucide-react';
import type { MachineType } from '../../types/machine-setup-types';

interface MachineTypeStepProps {
  machineTypes: MachineType[];
  selectedKey: string;
  selectedCategory: string | null;
  isLoading: boolean;
  error: string | null;
  onCategorySelect: (cat: string | null) => void;
  onSelect: (key: string, name: string) => void;
}

interface CategoryMeta {
  label: string;
  description: string;
  icon: ReactNode;
}

const CATEGORY_META: Record<string, CategoryMeta> = {
  media4: {
    label: 'Media 4',
    description: '4-inch touchscreen kiosk for self-service EV payments',
    icon: <Monitor className="h-6 w-6" />,
  },
  media4_mini: {
    label: 'Media 4 Mini',
    description: 'EV charging station / kiosk',
    icon: <Zap className="h-6 w-6" />,
  },
  vpost: {
    label: 'VPOST',
    description: 'EV meter charging units',
    icon: <Cpu className="h-6 w-6" />,
  },
  media5: {
    label: 'Media 5',
    description: '5-inch touchscreen kiosk for self-service EV payments',
    icon: <Tv2 className="h-6 w-6" />,
  },
  third_party: {
    label: 'Third Party Charger',
    description: 'OCPP-compatible chargers from external manufacturers',
    icon: <Plug className="h-6 w-6" />,
  },
};

// Map old category values (pre-device-rename) to current device keys
const LEGACY_CATEGORY_MAP: Record<string, string> = {
  charger: 'media4_mini',
  kiosk: 'media4',
};
const resolveDevice = (cat: string | undefined): string =>
  LEGACY_CATEGORY_MAP[cat ?? ''] ?? cat ?? 'media4_mini';

export function MachineTypeStep({
  machineTypes,
  selectedKey,
  selectedCategory,
  isLoading,
  error,
  onCategorySelect,
  onSelect,
}: MachineTypeStepProps) {
  // Auto-select category from selected key when machineTypes arrive after mount
  useEffect(() => {
    if (selectedKey && !selectedCategory) {
      const found = machineTypes.find(t => t.key === selectedKey);
      if (found) onCategorySelect(resolveDevice(found.category));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [machineTypes]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        <span className="ml-3 text-gray-600">Loading machine types…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-12 text-center">
        <p className="text-sm text-red-600 bg-red-50 rounded-md px-4 py-3 border border-red-200 inline-block">
          Failed to load machine types: {error}
        </p>
      </div>
    );
  }

  if (machineTypes.length === 0) {
    return (
      <div className="py-12 text-center text-sm text-gray-500">
        No machine types available.
      </div>
    );
  }

  // Derive distinct categories in declaration order; default to "charger" if API doesn't send it yet.
  // Always include "third_party" — it is not backed by machine types from the API.
  const DEVICE_ORDER = ['media4', 'media4_mini', 'media5', 'vpost', 'third_party'];
  const fromTypes = [...new Set(machineTypes.map(t => resolveDevice(t.category)))];
  const categories = [...DEVICE_ORDER.filter(d => d === 'third_party' || fromTypes.includes(d))];

  // ── Phase 1: category selection ──────────────────────────────────────────────
  if (!selectedCategory) {
    return (
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-1">Select Device</h2>
        <p className="text-sm text-gray-500 mb-6">Choose the device you want to set up.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {categories.map(cat => {
            const meta = CATEGORY_META[cat] ?? { label: cat, description: '', icon: <Zap className="h-6 w-6" /> };
            const isThirdParty = cat === 'third_party';
            const count = machineTypes.filter(t => resolveDevice(t.category) === cat).length;
            return (
              <button
                key={cat}
                onClick={() => onCategorySelect(cat)}
                className="text-left rounded-lg border-2 border-gray-200 bg-white p-5 hover:border-primary/60 hover:shadow-sm transition-all"
              >
                <div className="flex items-start gap-4">
                  <div className="shrink-0 flex items-center justify-center w-11 h-11 rounded-lg bg-primary/10 text-primary">
                    {meta.icon}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{meta.label}</p>
                    <p className="text-sm text-gray-500 mt-0.5">{meta.description}</p>
                    {!isThirdParty && (
                      <p className="text-xs text-gray-400 mt-2">
                        {count} {count === 1 ? 'type' : 'types'} available
                      </p>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // ── Phase 2: type selection within the chosen category ───────────────────────
  const filteredTypes = machineTypes.filter(t => resolveDevice(t.category) === selectedCategory);
  const meta = CATEGORY_META[selectedCategory] ?? { label: selectedCategory, description: '', icon: null };

  // Split charger types (machineTypeId 555007) from kiosk types (machineTypeId 996232251)
  const chargerTypes = filteredTypes.filter(t => t.machineTypeId === 555007);
  const kioskTypes   = filteredTypes.filter(t => t.machineTypeId === 996232251);
  const otherTypes   = filteredTypes.filter(t => t.machineTypeId !== 555007 && t.machineTypeId !== 996232251);
  const hasBothGroups = chargerTypes.length > 0 && kioskTypes.length > 0;

  const renderTypeCard = (type: (typeof filteredTypes)[number]) => {
    const isSelected = type.key === selectedKey;
    return (
      <button
        key={type.key}
        onClick={() => onSelect(type.key, type.displayName)}
        className={[
          'text-left rounded-lg border-2 p-4 transition-all',
          isSelected
            ? 'border-primary bg-primary/5 shadow-sm'
            : 'border-gray-200 bg-white hover:border-primary/50 hover:shadow-sm',
        ].join(' ')}
      >
        <p className="font-medium text-gray-900">{type.displayName}</p>
        <p className="text-xs text-gray-400 mt-1 font-mono">{type.key}</p>
      </button>
    );
  };

  return (
    <div>
      <h2 className="text-xl font-semibold text-gray-900 mb-1">{meta.label}</h2>
      <p className="text-sm text-gray-500 mb-6">Choose the specific model to create.</p>
      {hasBothGroups ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {chargerTypes.map(renderTypeCard)}
          </div>
          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 border-t border-gray-200" />
            <span className="text-xs text-gray-400 font-medium uppercase tracking-wide">Kiosk</span>
            <div className="flex-1 border-t border-gray-200" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {kioskTypes.map(renderTypeCard)}
          </div>
          {otherTypes.length > 0 && (
            <>
              <div className="flex items-center gap-3 my-6">
                <div className="flex-1 border-t border-gray-200" />
                <span className="text-xs text-gray-400 font-medium uppercase tracking-wide">Other</span>
                <div className="flex-1 border-t border-gray-200" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {otherTypes.map(renderTypeCard)}
              </div>
            </>
          )}
        </>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTypes.map(renderTypeCard)}
        </div>
      )}
    </div>
  );
}
